/**
 * Opening Sync Utilities
 *
 * Centralizes the read-side resolver and structure-side write helpers
 * for door / window / cased-opening storage post-Phase 5. Structure
 * openings are canonical for everything except identity, FK, and
 * parent-wall membership; room copies hold only those three.
 *
 * Side A / Side B convention (per-side trim + sill fields):
 *   Side A is the +Z face of the opening's local frame.
 *   Side B is the -Z face.
 *   Which room sits on which side is mediated by `wallFacesPriorityRoom`
 *   — that flag is relevant for door-swing direction, not for trim
 *   rendering. Renderers (DoorWithTrim, WindowWithTrim) read per-side
 *   fields by name and draw each face independently; they do not need
 *   to consult `wallFacesPriorityRoom` to decide which side to render.
 */

const {
  DOOR_TRIM_FIELDS,
  WINDOW_TRIM_FIELDS,
  TRIM_FIELDS_BY_CATEGORY,
  APPEARANCE_FIELDS_BY_CATEGORY,
  pickFields,
  buildOpeningLookup,
  hydrateRoomOpenings,
  resolveRoomOpening,
} = require('../../openings/roomOpeningHydration');

/**
 * Check whether a room-level element is a copy of a given structure opening.
 */
const isRoomCopyOf = (roomElement, structureId) =>
  roomElement?.id === structureId ||
  roomElement?.structureOpeningId === structureId;

/**
 * A demoed wall is an opening subtype: category.opening.demoed === true.
 * (The legacy `category.demoedWall` sibling shape was retired by the
 * migrate-demoed-wall-subtype data migration.)
 */
const isDemoedOpening = (item) => item?.category?.opening?.demoed === true;

/**
 * Is this door, window or passage COMING OUT — marked for demolition (the
 * demo scope's own `demoed: true`), or itself a demolished stretch of wall
 * (`isDemoedOpening`)? Two facts, kept apart on the element; either means no
 * opening stays to be framed or reframed.
 */
const isOpeningComingOut = (item) =>
  item?.demoed === true || isDemoedOpening(item);

/**
 * A Whole Wall demolition the room merge could not take (an exterior wall, a
 * wall bordering three rooms, a merge that threw) leaves the wall standing
 * with a full-height demoed opening over it — a wall torn out, which pricing
 * bills by length (claude-docs/2026-10-22-wall-work-pricing-design.md §4).
 * Part Wall writes the same demoed opening, so the kind is marked; a record
 * written before the marker is read by its id prefix.
 */
const WHOLE_WALL_DEMO_KIND = 'whole-wall';
const isWholeWallDemo = (item) =>
  isDemoedOpening(item) &&
  (item.demoKind === WHOLE_WALL_DEMO_KIND ||
    String(item.id ?? '').startsWith('wholeWall_'));

/**
 * Map over an array of structure elements, applying an updater function
 * to the element matching the given ID.
 *
 * @param {Array} array - The structure array (e.g. data.doors)
 * @param {string} id - The element ID to match
 * @param {Function} updater - (element) => updatedElement
 * @returns {Array} New array with the matched element updated
 */
const updateStructureArray = (array, id, updater) => {
  if (!array) return array;
  return array.map((el) => (el?.id === id ? updater(el) : el));
};

/**
 * Collect the set of structure-opening ids that have at least one room-level
 * copy across the given rooms — i.e. the openings those rooms "own". Walks
 * each room's slim door / window / opening copies and reads their
 * `structureOpeningId` FK. Copies without an FK are skipped (they have no
 * canonical counterpart to own).
 */
const collectOwnedStructureOpeningIds = (rooms) => {
  const owned = new Set();
  for (const room of rooms || []) {
    for (const key of ['doors', 'windows', 'openings']) {
      for (const copy of room?.[key] || []) {
        if (copy?.structureOpeningId) owned.add(copy.structureOpeningId);
      }
    }
  }
  return owned;
};

// Accept a Map<id, room> (the UVFloorPlanPage shape) or a plain array.
const roomListOf = (rooms) =>
  rooms instanceof Map ? Array.from(rooms.values()) : rooms || [];

/**
 * Map each structure-opening id → the set of room ids that reference it. An
 * opening is *shared* (interior) when more than one room references it. Drives
 * the multi-room write + the "also updates N other room(s)" panel note for
 * single-room opening edits (see the 2026-06-12 plan §1.2).
 *
 * @param {Map<string,Object>|Array<Object>} rooms
 * @returns {Map<string, Set<string>>}
 */
const buildOpeningRoomMembership = (rooms) => {
  const membership = new Map();
  for (const room of roomListOf(rooms)) {
    const roomId = room?.id;
    if (!roomId) continue;
    for (const key of ['doors', 'windows', 'openings']) {
      for (const copy of room?.[key] || []) {
        const sid = copy?.structureOpeningId;
        if (!sid) continue;
        if (!membership.has(sid)) membership.set(sid, new Set());
        membership.get(sid).add(roomId);
      }
    }
  }
  return membership;
};

/** True iff the structure opening is referenced by more than one room. */
const isOpeningShared = (structureOpeningId, membership) => {
  const rooms = membership?.get?.(structureOpeningId);
  return !!rooms && rooms.size > 1;
};

/**
 * For a single room, index its measured (door + window) opening copies. Only
 * doors and windows are measured in v1 — frameless openings are excluded so a
 * 'pier' endpoint id never resolves to one. Returns the set of copy ids (for
 * endpoint-kind resolution) and a copy id → structure opening id map (falling
 * back to the copy id itself for legacy rows with no FK, mirroring
 * resolveRoomOpening).
 *
 * @param {Object} room
 * @returns {{ copyIds: Set<string>, structureOpeningIdByCopyId: Map<string,string> }}
 */
const buildRoomMeasuredOpeningIndex = (room) => {
  const copyIds = new Set();
  const structureOpeningIdByCopyId = new Map();
  for (const key of ['doors', 'windows']) {
    for (const copy of room?.[key] || []) {
      if (!copy?.id) continue;
      copyIds.add(copy.id);
      structureOpeningIdByCopyId.set(
        copy.id,
        copy.structureOpeningId || copy.id
      );
    }
  }
  return { copyIds, structureOpeningIdByCopyId };
};

/**
 * Filter canonical structure openings down to those that should render in a
 * single room's (dollhouse) view.
 *
 * A structure opening is kept when its parent wall belongs to the selected
 * room (`allowedStructureWallIds`) AND either:
 *   - the selected room owns a copy of it (`ownedByThisRoom`), or
 *   - it is a true orphan — no room owns a copy anywhere (`!ownedByAnyRoom`),
 *     e.g. a freshly-placed opening / demoed wall on a structure wall that has
 *     no aligned room wall.
 *
 * The "owned by this room OR orphan" clause is what stops a *neighbor's*
 * opening from leaking through when two rooms share a structure wall: the
 * shared wall is "allowed" for both rooms, but the neighbor's opening is owned
 * by the neighbor, not the selected room, so it is dropped. Without all rooms
 * in scope this distinction can't be made — which is exactly why the dollhouse
 * structure-fallback in ArchitecturalScene couldn't make it (see that file's
 * note on retiring `coveredStructureIds`).
 */
const filterStructureOpeningsForRoomView = (
  openings,
  { allowedStructureWallIds, ownedByThisRoom, ownedByAnyRoom }
) =>
  (openings || []).filter((o) => {
    const parentWallId = o?.parentWallIds?.[0];
    if (!allowedStructureWallIds.has(parentWallId)) return false;
    return ownedByThisRoom.has(o.id) || !ownedByAnyRoom.has(o.id);
  });

/**
 * Can this opening actually be DRAWN, or would the renderer have to invent it?
 *
 * Geometry lives only on the canonical structure entry. A slim room / exterior
 * copy naming a canonical that no longer exists comes back out of
 * `resolveRoomOpening` unresolved — the P1 fallback returns the copy as-is —
 * and a slim copy has no `center`, no `dimensions` and no `rotation`.
 *
 * That does NOT render as nothing, which is the trap. Every one of the three
 * components fills the hole in with a plausible default — `Door.jsx` 0.9 x 2.0,
 * `Window.jsx` 0.8 x 1.5, `Opening.jsx` 0.9 x 2.1 — and `extractTransformProps`
 * returns `[0, 0, 0]` for a missing centre. So an opening whose canonical was
 * deleted draws as a correctly-proportioned doorway standing free at the WORLD
 * ORIGIN, usually in the middle of a room. `AC2744E5` ("New Montrose / Scan 28")
 * is where it was first noticed; 53 such copies existed across 19 of the 55
 * corpus structures, every one of them drawing something.
 *
 * The producer is fixed (`functions/features/opening-copy-sync.js` reconciles
 * the copies whenever a pass removes a canonical), but this predicate is what
 * makes the failure mode structurally impossible rather than merely absent:
 * whatever leaves a copy dangling — old data not yet reprocessed, a partial
 * delete, a future pass nobody has written — the renderer draws nothing instead
 * of inventing a door.
 *
 * Deliberately a test of the GEOMETRY, not of "did the FK resolve". Legacy fat
 * room copies carry their own `center` / `dimensions` and pass, which is
 * exactly the case `resolveRoomOpening`'s fallback exists to serve.
 *
 * @param {Object} opening - a resolved opening (canonical, or a room copy
 *   hydrated against one)
 * @returns {boolean}
 */
const hasRenderableOpeningGeometry = (opening) => {
  const finite = (v) => typeof v === 'number' && Number.isFinite(v);
  const c = opening?.center;
  if (!c || !finite(c.x) || !finite(c.y) || !finite(c.z)) return false;
  // Arrays and `{x,y,z}` both occur — the components accept either.
  const d = opening?.dimensions;
  const [w, h] = Array.isArray(d) ? [d[0], d[1]] : [d?.x, d?.y];
  return finite(w) && finite(h) && w > 0 && h > 0;
};

/**
 * Hydrate a room against the RENDERER's structure data — i.e. the value of
 * `data` in StructureViewerWrapper, which carries `structureDoors /
 * structureWindows / structureOpenings`. Returns the room, not a flat list.
 *
 * Use this at the boundary of anything that plans from a room's geometry.
 * Post-PR #634 the viewer's room copies are SLIM (`{id, structureOpeningId,
 * parentWallIds}`), so a planner reading `window.center` off one silently gets
 * `undefined` and defaults it — the dorm decor planner put curtains at the
 * room's corner instead of on the window this way.
 */

/**
 * Convenience helper: return a single combined `[...doors, ...windows,
 * ...openings]` array for a room, with each entry hydrated against the
 * canonical structure-level arrays. Pass the renderer's `structureData`
 * (i.e. the value of `data` in StructureViewerWrapper) — this reads
 * `structureDoors / structureWindows / structureOpenings` off it.
 *
 * Use this at any 3D-render call site that previously did
 *   `[...(room.doors||[]), ...(room.windows||[]), ...(room.openings||[])]`
 * and then iterated `.center` / `.dimensions` / `.category` — post-PR
 * #634 those reads are undefined on slim room copies.
 */
const getHydratedRoom = (room, structureData) => {
  if (!room) return room;
  return hydrateRoomOpenings(room, {
    doors: structureData?.structureDoors || [],
    windows: structureData?.structureWindows || [],
    openings: structureData?.structureOpenings || [],
  });
};

/**
 * A hydrated room with every WINDOW or generic opening that did not resolve to
 * real geometry dropped. Twin of `withResolvedOpeningsOnly` in
 * `shared-lib/openings/openingSync.js` — keep the two in step.
 *
 * For CONSUMERS that read opening geometry (the dorm decor planners). NOT for
 * the sync paths, which need to see an unresolved copy in order to repair it.
 *
 * **Doors are deliberately kept, resolved or not** — see the shared twin for
 * the full reasoning. In short: nothing reads a door's POSITION, only which
 * wall it belongs to, and that attribution works on a slim copy via
 * `parentWallIds`. Dropping them stopped `cornerTouchesDoor` rejecting the
 * doorway corners, so floor decor was placed in the doorway.
 */
const withRenderableOpeningsOnly = (room) => {
  if (!room) return room;
  const keep = (arr) =>
    Array.isArray(arr) ? arr.filter(hasRenderableOpeningGeometry) : arr;
  return {
    ...room,
    windows: keep(room.windows),
    openings: keep(room.openings),
  };
};

const getHydratedRoomOpenings = (room, structureData) => {
  if (!room) return [];
  const hydrated = getHydratedRoom(room, structureData);
  return [
    ...(hydrated.doors || []),
    ...(hydrated.windows || []),
    ...(hydrated.openings || []),
  ];
};

/**
 * Apply ONLY appearance fields to a structure opening, at `category[type]`
 * directly (flat). Caller passes a flat settings object that may mix
 * appearance and trim; this helper picks the appearance subset listed in
 * `APPEARANCE_FIELDS_BY_CATEGORY` so trim never leaks onto the structure
 * copy. The room-side equivalent is `applyTrimToRoomOpening`. The legacy
 * nested `doorStyle` / `windowStyle` sub-object is hoisted to flat by the
 * migrate-opening-shape Phase 5 appearance-hoist pass.
 */
const applyAppearanceToStructureOpening = (
  structureOpening,
  settings,
  type
) => {
  const categoryKey = type;
  const allowed = APPEARANCE_FIELDS_BY_CATEGORY[categoryKey] || [];
  const appearance = pickFields(settings, allowed);

  return {
    ...structureOpening,
    category: {
      ...structureOpening.category,
      [categoryKey]: {
        ...structureOpening.category?.[categoryKey],
        ...appearance,
      },
    },
  };
};

/**
 * Apply ONLY trim fields to a room opening, at `category[type]` directly
 * (the hoisted location — not nested inside doorStyle / windowStyle).
 * Caller passes a flat settings object that may mix appearance and trim;
 * this helper picks the trim subset listed in `TRIM_FIELDS_BY_CATEGORY`
 * so appearance never leaks onto the room copy. The structure-side
 * equivalent is `applyAppearanceToStructureOpening`.
 */
const applyTrimToRoomOpening = (roomOpening, settings, type) => {
  const categoryKey = type;
  const allowed = TRIM_FIELDS_BY_CATEGORY[categoryKey] || [];
  const trim = pickFields(settings, allowed);

  return {
    ...roomOpening,
    category: {
      ...roomOpening.category,
      [categoryKey]: {
        ...roomOpening.category?.[categoryKey],
        ...trim,
      },
    },
  };
};

// Legacy trim names recognized by the hoist helpers (for sweeping
// pre-Phase-5 data). Post-Phase-5 these all live on structure under
// per-side variants; the migration handles the schema expansion.
const LEGACY_HOIST_TRIM_BY_CATEGORY = {
  door: [
    'trimStyle',
    'trimStyleSideA',
    'trimStyleSideB',
    'trimColorSideA',
    'trimColorSideB',
  ],
  window: [
    'trimStyle',
    'trimStyleSideA',
    'trimStyleSideB',
    'trimColor',
    'trimColorSideA',
    'trimColorSideB',
    'trimWidth',
    'trimWidthSideA',
    'trimWidthSideB',
    'trimDepth',
    'trimDepthSideA',
    'trimDepthSideB',
    'sillDepth',
    'sillDepthSideA',
    'sillDepthSideB',
    'sillColor',
    'sillColorSideA',
    'sillColorSideB',
  ],
};

/**
 * Extract trim fields from a nested `doorStyle` / `windowStyle`
 * sub-object on a room opening. Returns a flat trim-only object suitable
 * for hoisting, or `null` when no nested trim is present.
 *
 * Used by legacy-shape migration paths only — the runtime trim inventory
 * (`TRIM_FIELDS_BY_CATEGORY`) is empty post-Phase-5, so this helper
 * carries its own list of legacy trim names.
 */
const extractTrimFromStyle = (opening, type) => {
  const categoryKey = type;
  const styleKey = type === 'door' ? 'doorStyle' : 'windowStyle';
  const style = opening?.category?.[categoryKey]?.[styleKey];
  if (!style) return null;
  const allowed = LEGACY_HOIST_TRIM_BY_CATEGORY[categoryKey] || [];
  const trim = pickFields(style, allowed);
  return Object.keys(trim).length ? trim : null;
};

/**
 * Hoist trim fields from a nested `doorStyle` / `windowStyle` up to
 * `category[type]` directly. Returns a new opening with trim moved and
 * the nested copies removed. Idempotent.
 */
const hoistTrimToCategory = (opening, type) => {
  const trim = extractTrimFromStyle(opening, type);
  if (!trim) return opening;
  const categoryKey = type;
  const styleKey = type === 'door' ? 'doorStyle' : 'windowStyle';
  const allowed = LEGACY_HOIST_TRIM_BY_CATEGORY[categoryKey] || [];
  const oldCategory = opening.category?.[categoryKey] || {};
  const oldStyle = oldCategory[styleKey] || {};
  const newStyle = { ...oldStyle };
  for (const f of allowed) delete newStyle[f];
  const newCategory = {
    ...oldCategory,
    ...trim,
  };
  if (Object.keys(newStyle).length) {
    newCategory[styleKey] = newStyle;
  } else {
    delete newCategory[styleKey];
  }
  return {
    ...opening,
    category: {
      ...opening.category,
      [categoryKey]: newCategory,
    },
  };
};

/**
 * The id an opening has in the STRUCTURE's own doors / windows / openings
 * arrays.
 *
 * A room keeps a slim copy of every opening with its OWN id and a
 * `structureOpeningId` pointing at the canonical entry — two disjoint id
 * spaces. `resolveRoomOpening` deliberately returns the room-copy id as `id`,
 * so a panel's `selectedObject.id` IS the room-copy one, and matching it
 * against a structure array never succeeds.
 *
 * That is what made a door or window STYLE edit silently not persist: the
 * handlers asked `doors.some((d) => d.id === selected.id)` to decide whether
 * the opening was structure-level, which was always false, so the write was
 * skipped. The panel still showed the new value because it had already
 * updated its own copy of the selection — the edit was gone the moment the
 * panel was reopened. Both hosts had it.
 */
const canonicalOpeningId = (opening) =>
  opening?.structureOpeningId ?? opening?.id ?? null;

/**
 * Patch the writable fields of an opening's CANONICAL record from a panel's
 * element-shaped update.
 *
 * The field list is the contract, and it is a allow-list on purpose: an
 * earlier version spread the whole updated element over the canonical
 * (`{...updatedElement, id: existing.id}`), and since panels hand back a
 * room copy plus a patch, that wholesale-replaced the canonical with a SLIM
 * shape — wiping its `dimensions`, `category` and `parentWallIds`, after
 * which the renderer's `!dimensions` filter dropped the opening and it
 * vanished from the drawing.
 *
 * Shared so the floor plan and the elevations viewer cannot drift on which
 * fields an opening edit is allowed to carry.
 *
 * @param {Object} existing       the canonical structure-level record
 * @param {Object} updatedElement the panel's element, carrying the changes
 * @returns {Object} a new record; untouched keys are preserved
 */
const patchCanonicalOpening = (existing, updatedElement) => {
  const next = { ...existing };
  for (const key of ['center', 'dimensions', 'rotation', 'name', 'category']) {
    if (updatedElement?.[key] !== undefined) next[key] = updatedElement[key];
  }
  return next;
};

/**
 * Apply one appearance to MANY openings, in a single pass.
 *
 * The style handlers used to take one id and were therefore unsafe to call in
 * a loop: each call read `structureData` from its closure, so every iteration
 * started from the same stale base and the last write won — in local state AND
 * in the array handed to `updateStructureChildElements`, which meant a
 * multi-select style apply would have persisted only the final opening and
 * silently dropped the rest.
 *
 * Taking the ids together removes the loop rather than making it safe: one
 * derivation, one state write, one persist.
 *
 * @param {Array}    array    the structure's doors / windows / openings
 * @param {string[]} ids      every element to style
 * @param {Object}   settings flat settings; the appearance subset is picked
 * @param {'door'|'window'|'opening'} type
 * @returns {Array} a new array, or `array` untouched when nothing matches
 */
const applyAppearanceToStructureOpenings = (array, ids, settings, type) => {
  if (!array || !ids?.length) return array;
  const wanted = new Set(ids.filter(Boolean));
  if (!wanted.size) return array;
  return array.map((el) =>
    el?.id && wanted.has(el.id)
      ? applyAppearanceToStructureOpening(el, settings, type)
      : el
  );
};

module.exports = {
  isOpeningComingOut,
  DOOR_TRIM_FIELDS,
  WINDOW_TRIM_FIELDS,
  isRoomCopyOf,
  isDemoedOpening,
  WHOLE_WALL_DEMO_KIND,
  isWholeWallDemo,
  updateStructureArray,
  TRIM_FIELDS_BY_CATEGORY,
  APPEARANCE_FIELDS_BY_CATEGORY,
  collectOwnedStructureOpeningIds,
  buildOpeningRoomMembership,
  isOpeningShared,
  buildRoomMeasuredOpeningIndex,
  filterStructureOpeningsForRoomView,
  hasRenderableOpeningGeometry,
  buildOpeningLookup,
  hydrateRoomOpenings,
  getHydratedRoom,
  withRenderableOpeningsOnly,
  getHydratedRoomOpenings,
  resolveRoomOpening,
  applyAppearanceToStructureOpening,
  applyTrimToRoomOpening,
  extractTrimFromStyle,
  hoistTrimToCategory,
  canonicalOpeningId,
  patchCanonicalOpening,
  applyAppearanceToStructureOpenings,
};
